import type { AssignTargetOption } from "@/features/DragonTiles/store/parentTileView";
import type { DragonTile, Frame, InnerTile } from "@/types/dragon-tiles";

import { getAudienceId } from "@/features/DragonTiles/audienceInnerTiles";
import { ParentTileViewStore } from "@/features/DragonTiles/store/parentTileView";

/** Keep the sidebar focused on the audience bucket used for an action. */
export function syncSelectedAudienceForInnerTile(
  dragonTile: DragonTile,
  innerTileId: string,
): void {
  const innerTile = dragonTile.inner_tiles.find((it) => it.id === innerTileId);
  if (!innerTile) return;
  const audienceId = getAudienceId(innerTile);
  ParentTileViewStore.actions.setSelectedAudienceId(audienceId || null);
}

export function getParentPoolInnerTile(
  dragonTile: DragonTile,
): InnerTile | undefined {
  return dragonTile.inner_tiles.find((it) => !getAudienceId(it));
}

/**
 * Split audience inner tiles into the currently selected bucket (if any) and
 * the remaining audiences, preserving relative order within each group.
 *
 * Used so Global shows parent pool then all audiences, while a selected
 * audience shows that audience first, then parent pool, then others — enabling
 * cross-audience copy/pick from either context.
 */
export function partitionAudienceInnerTiles(
  audienceInnerTiles: InnerTile[],
  selectedAudienceId: string | null,
): { selected: InnerTile[]; others: InnerTile[] } {
  if (!selectedAudienceId) {
    return { selected: [], others: audienceInnerTiles };
  }

  const selected: InnerTile[] = [];
  const others: InnerTile[] = [];
  for (const it of audienceInnerTiles) {
    if (getAudienceId(it) === selectedAudienceId) {
      selected.push(it);
    } else {
      others.push(it);
    }
  }
  return { selected, others };
}

export function buildAssignTargetOptions(
  sourceInnerTileId: string,
  parentPoolInnerTile: InnerTile | undefined,
  audienceInnerTiles: InnerTile[],
): AssignTargetOption[] {
  const options: AssignTargetOption[] = [];
  if (parentPoolInnerTile && parentPoolInnerTile.id !== sourceInnerTileId) {
    options.push({ innerTileId: parentPoolInnerTile.id, label: "Parent pool" });
  }
  for (const innerTile of audienceInnerTiles) {
    if (innerTile.id === sourceInnerTileId) continue;
    options.push({ innerTileId: innerTile.id, label: innerTile.name });
  }
  return options;
}

export function mapFramesById(
  frames: Frame[],
  additionalFrames: Frame[] = [],
): Map<string, Frame> {
  const map = new Map<string, Frame>();
  for (const frame of frames) {
    map.set(frame.id, frame);
  }
  for (const frame of additionalFrames) {
    map.set(frame.id, frame);
  }
  return map;
}
